import { useState } from 'react';

import { useContactSources, useCreateContactSource, useDeleteContactSource, useRenameContactSource } from '../../hooks/use-contacts';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../ui/toast';

/** U3.3: source dictionary CRUD, owner/lead only (enforced server-side via `contact_sources.manage`; this UI assumes the caller already gated on role). */
export function ContactSourcesSection({ canManage }: { canManage: boolean }) {
  const sources = useContactSources();
  const create = useCreateContactSource();
  const rename = useRenameContactSource();
  const del = useDeleteContactSource();
  const toast = useToast();
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await create.mutateAsync(newTitle.trim());
      setNewTitle('');
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Не удалось добавить источник');
    }
  }

  function startEdit(id: string, title: string) {
    setEditingId(id);
    setEditingTitle(title);
  }

  async function submitRename() {
    if (!editingId || !editingTitle.trim()) return;
    try {
      await rename.mutateAsync({ id: editingId, title: editingTitle.trim() });
      setEditingId(null);
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Не удалось переименовать источник');
    }
  }

  async function remove(id: string) {
    try {
      await del.mutateAsync(id);
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Не удалось удалить источник');
    }
  }

  return (
    <div className="max-w-lg space-y-3">
      <h2 className="text-sm font-semibold">Источники контактов</h2>
      <p className="text-xs text-muted-fg">
        Встроенные значения (telegram, parsing, crm_base, manual, vk_ads, other) нельзя переименовать или удалить.
      </p>

      <ul className="divide-y divide-border rounded border border-border">
        {(sources.data?.sources ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
            {editingId === s.id ? (
              <Input
                aria-label="Новое название источника"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                className="h-8"
              />
            ) : (
              <span className="flex items-center gap-2">
                {s.title}
                {s.isBuiltin && <Badge>встроенный</Badge>}
              </span>
            )}
            {canManage && !s.isBuiltin && (
              <div className="flex shrink-0 gap-1">
                {editingId === s.id ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => void submitRename()}>
                      Сохранить
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                      Отмена
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => startEdit(s.id, s.title)}>
                      Переименовать
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void remove(s.id)}>
                      Удалить
                    </Button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {canManage && (
        <form onSubmit={(e) => void submitCreate(e)} className="flex gap-2">
          <Input
            aria-label="Название нового источника"
            placeholder="Например: Партнёрская программа"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <Button type="submit" disabled={!newTitle.trim() || create.isPending}>
            Добавить
          </Button>
        </form>
      )}
    </div>
  );
}
